// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";

// Types
import { ClinicUserType } from "src/services/clinics/types";

// Utils
import { isValidEmail } from "src/utils/text/textHelper";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

type AddUserModalType = {
  open: boolean;
  usersData: Array<ClinicUserType>;
  onClose: () => void;
  onConfirm: (userData: ClinicUserType) => void;
};

const AddUserModal = ({
  open,
  usersData,
  onClose,
  onConfirm,
}: AddUserModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [userEmail, setUserEmail] = useState("");
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const val = event.target.value;
      setIsInvalidEmail(!isValidEmail(val));
      setUserAlreadyExists(
        Boolean(usersData.find((user) => user.email === val))
      );
      setUserEmail(val);
    },
    [usersData, setUserEmail]
  );

  const errorText = useMemo(
    () =>
      userAlreadyExists
        ? t(
            "The email address you entered is already in use. Please choose a different one to proceed."
          )
        : isInvalidEmail
          ? t("The email address you entered is invalid.")
          : undefined,
    [userAlreadyExists, isInvalidEmail, t]
  );

  const resetModalValues = useCallback(() => {
    setUserEmail("");
    setUserAlreadyExists(false);
    setIsInvalidEmail(false);
  }, []);

  const handleClose = useCallback(() => {
    resetModalValues();
    onClose();
  }, [resetModalValues, onClose]);

  const handleConfirm = useCallback(() => {
    const newUser: ClinicUserType = {
      email: userEmail,
    };

    resetModalValues();
    onConfirm(newUser);
  }, [resetModalValues, onConfirm, userEmail]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Add User")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            data-testid="button_add"
            sx={ModalCustomConfirm}
            onClick={handleConfirm}
            disabled={!userEmail || userAlreadyExists || isInvalidEmail}
          >
            {t("Add")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        <Box display="flex" flexDirection="column" gap="8px">
          <Typography fontSize="14px">
            {t("User Email")}
            <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
          </Typography>
          <TextInputField
            id="add-user-email-input"
            required
            placeholder={t("Enter a value")}
            value={userEmail}
            onChange={handleChange}
            error={userAlreadyExists || isInvalidEmail}
            helperText={errorText}
            maxLength={50}
          />
        </Box>
      </Grid>
    </DefaultModal>
  );
};

export default AddUserModal;
